import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Search, Eye, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_COLORS = {
  confirmed: { bg: '#dcfce7', color: '#166534' },
  pending_payment: { bg: '#fef3c7', color: '#92400e' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
  expired: { bg: '#e5e7eb', color: '#6b7280' },
  checked_in: { bg: '#dbeafe', color: '#1e40af' },
  checked_out: { bg: '#f3f4f6', color: '#374151' },
};

export default function AdminReservations() {
  const { apiHeaders, language, formatPrice, t, BACKEND_URL } = useApp();
  const [reservations, setReservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRes, setSelectedRes] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const STATUSES = ['all', 'pending_payment', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'expired'];
  const statusLabel = {
    all: language === 'tr' ? 'Tümü' : 'All',
    confirmed: t('status_confirmed'), pending_payment: t('status_pending_payment'),
    cancelled: t('status_cancelled'), checked_in: t('status_checked_in'), checked_out: t('status_checked_out'),
    expired: language === 'tr' ? 'Süresi Doldu' : 'Expired',
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await axios.get(`${BACKEND_URL}/api/reservations?${params.toString()}`, apiHeaders());
      setReservations(res.data.reservations);
      setTotal(res.data.total);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchReservations(); }, [statusFilter]);

  const nights = (res) => {
    if (!res?.check_in || !res?.check_out) return 0;
    return Math.round((new Date(res.check_out) - new Date(res.check_in)) / (1000 * 60 * 60 * 24));
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/reservations/${id}/status`, { status: newStatus }, apiHeaders());
      toast.success(language === 'tr' ? 'Durum güncellendi' : 'Status updated');
      fetchReservations();
      if (selectedRes?._id === id) setSelectedRes(prev => ({ ...prev, status: newStatus }));
    } catch { toast.error('Error'); }
    setUpdating(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm(language === 'tr' ? 'Bu rezervasyon iptal edilsin mi?' : 'Cancel this reservation?')) return;
    await handleStatusUpdate(id, 'cancelled');
    setDetailOpen(false);
  };

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '2px' }}>
          {t('reservations')}
        </h1>
        <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{total} {language === 'tr' ? 'rezervasyon' : 'reservations'}</p>
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#B08D57' }} />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchReservations()}
            placeholder={language === 'tr' ? 'Ref, misafir adı veya e-posta...' : 'Ref, guest name or email...'}
            style={{ border: '1px solid #D8D1C6', borderRadius: '10px', paddingLeft: '36px', fontSize: '13px' }}
          />
        </div>
        <button onClick={fetchReservations} style={{ background: '#1F3A2E', color: 'white', padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
          {language === 'tr' ? 'Ara' : 'Search'}
        </button>
      </div>

      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {STATUSES.map(s => {
          const sc = STATUS_COLORS[s] || {};
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                cursor: 'pointer', border: '1.5px solid', fontFamily: 'Manrope, sans-serif',
                background: statusFilter === s ? (sc.bg || '#1F3A2E') : 'white',
                color: statusFilter === s ? (sc.color || 'white') : '#6E7F5B',
                borderColor: statusFilter === s ? (sc.color || '#1F3A2E') : '#D8D1C6',
              }}>
              {statusLabel[s]}
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1,2,3,4].map(i => <Skeleton key={i} style={{ height: '60px', borderRadius: '10px' }} />)}
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', overflow: 'hidden' }} data-testid="admin-reservations-table">
          {reservations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#B0A99A', fontSize: '14px' }}>
              {language === 'tr' ? 'Rezervasyon bulunamadı' : 'No reservations found'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F9F7F4', borderBottom: '1px solid #EFE7DA' }}>
                  {[
                    language === 'tr' ? 'Referans' : 'Reference',
                    language === 'tr' ? 'Misafir' : 'Guest',
                    language === 'tr' ? 'Oda' : 'Room',
                    language === 'tr' ? 'Tarihler' : 'Dates',
                    language === 'tr' ? 'Durum' : 'Status',
                    language === 'tr' ? 'Tutar' : 'Amount',
                    ''
                  ].map((h, i) => (
                    <th key={i} style={{ textAlign: i === 5 ? 'right' : 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reservations.map(res => {
                  const sc = STATUS_COLORS[res.status] || { bg: '#f3f4f6', color: '#374151' };
                  return (
                    <tr key={res._id} style={{ borderBottom: '1px solid #F6F1E7', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 14px' }}>
                        <code style={{ fontSize: '11px', background: '#F6F1E7', padding: '3px 7px', borderRadius: '5px', color: '#3E5A3F', fontFamily: 'monospace' }}>{res.booking_ref}</code>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <p style={{ fontWeight: 600, color: '#1F3A2E', marginBottom: '1px' }}>{res.first_name} {res.last_name}</p>
                        <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{res.email}</p>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#5A5450', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.room_name}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <p style={{ color: '#1F3A2E', whiteSpace: 'nowrap' }}>{res.check_in} → {res.check_out}</p>
                        <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{nights(res)} {t('nights')}</p>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: 600, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
                          {statusLabel[res.status] || res.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#1F3A2E', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatPrice(res.total_price_try)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <button onClick={() => { setSelectedRes(res); setDetailOpen(true); }}
                          style={{ background: 'transparent', border: '1px solid #D8D1C6', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Eye size={13} style={{ color: '#6E7F5B' }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail Modal - Custom overlay (no Radix) */}
      {detailOpen && selectedRes && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setDetailOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto', padding: '24px', fontFamily: 'Manrope, sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: 600, color: '#1F3A2E' }}>
                {language === 'tr' ? 'Rezervasyon Detayı' : 'Reservation Detail'}
              </h2>
              <button onClick={() => setDetailOpen(false)} style={{ background: '#F6F1E7', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <X size={16} style={{ color: '#6E7F5B' }} />
              </button>
            </div>

            {/* Ref + Status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <code style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, color: '#1F3A2E', background: '#F6F1E7', padding: '6px 12px', borderRadius: '8px' }}>
                {selectedRes.booking_ref}
              </code>
              {(() => { const sc = STATUS_COLORS[selectedRes.status] || { bg: '#f3f4f6', color: '#374151' }; return <span style={{ fontSize: '12px', padding: '5px 14px', borderRadius: '20px', fontWeight: 700, background: sc.bg, color: sc.color }}>{statusLabel[selectedRes.status]}</span>; })()}
            </div>

            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px', paddingTop: '14px', borderTop: '1px solid #EFE7DA' }}>
              {[
                { label: language === 'tr' ? 'Misafir' : 'Guest', value: `${selectedRes.first_name} ${selectedRes.last_name}` },
                { label: 'Email', value: selectedRes.email },
                { label: language === 'tr' ? 'Telefon' : 'Phone', value: selectedRes.phone },
                { label: language === 'tr' ? 'Kişi' : 'Guests', value: selectedRes.guests },
                { label: language === 'tr' ? 'Oda' : 'Room', value: selectedRes.room_name },
                { label: language === 'tr' ? 'Gece' : 'Nights', value: nights(selectedRes) },
                { label: language === 'tr' ? 'Giriş' : 'Check-in', value: selectedRes.check_in },
                { label: language === 'tr' ? 'Çıkış' : 'Check-out', value: selectedRes.check_out },
              ].map(item => (
                <div key={item.label}>
                  <p style={{ fontSize: '10px', color: '#6E7F5B', marginBottom: '2px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</p>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: '#1F3A2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</p>
                </div>
              ))}
            </div>

            {selectedRes.special_requests && (
              <div style={{ background: '#F6F1E7', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px' }}>
                <p style={{ fontSize: '11px', color: '#6E7F5B', marginBottom: '2px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('special_requests')}</p>
                <p style={{ fontSize: '13px', color: '#3A3632' }}>{selectedRes.special_requests}</p>
              </div>
            )}

            {/* Total */}
            <div style={{ borderTop: '1px solid #EFE7DA', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '16px', marginBottom: '16px' }}>
              <span style={{ color: '#1F3A2E' }}>{t('total_price')}</span>
              <span style={{ color: '#1F3A2E', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(selectedRes.total_price_try)}</span>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedRes.status === 'pending_payment' && (
                <button disabled={updating} onClick={() => handleStatusUpdate(selectedRes._id, 'confirmed')}
                  style={{ flex: 1, minWidth: '120px', background: '#166534', color: 'white', padding: '11px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                  <CheckCircle size={14} /> {language === 'tr' ? 'Onayla' : 'Confirm'}
                </button>
              )}
              {selectedRes.status === 'confirmed' && (
                <button disabled={updating} onClick={() => handleStatusUpdate(selectedRes._id, 'checked_in')}
                  style={{ flex: 1, minWidth: '100px', background: '#1e40af', color: 'white', padding: '11px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                  Check-in
                </button>
              )}
              {selectedRes.status === 'checked_in' && (
                <button disabled={updating} onClick={() => handleStatusUpdate(selectedRes._id, 'checked_out')}
                  style={{ flex: 1, minWidth: '100px', background: '#374151', color: 'white', padding: '11px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                  Check-out
                </button>
              )}
              {!['cancelled', 'checked_out'].includes(selectedRes.status) && (
                <button disabled={updating} onClick={() => handleCancel(selectedRes._id)}
                  style={{ flex: 1, minWidth: '120px', background: 'white', color: '#dc2626', padding: '11px', borderRadius: '10px', border: '1.5px solid #fca5a5', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                  <X size={14} /> {language === 'tr' ? 'İptal Et' : 'Cancel'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
